/**
 * QuoteDetailsView - Full-page view for quote details
 */

import React, { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  Tabs,
  Tab,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  Download,
  Message,
  Cancel,
  Send,
  Schedule,
  ListAlt,
  Email,
  Receipt,
  Link as LinkIcon,
  Policy,
  Payments,
  VerifiedUser,
  ManageAccounts,
  FactCheck,
} from "@mui/icons-material";
import {
  getQuoteById,
  sendQuote,
  resendQuote,
  cancelQueuedQuoteSend,
} from "../services/quoteService";
import { quoteDownloadService } from "../services/downloadService";
import {
  createPolicyShareLink,
  emailPolicyAgreement,
  getQuotePolicy,
} from "../../systemSettings/services/quotePolicyService.js";
import QuoteStatusManager from "../components/QuoteStatusManager";
import PaymentProofViewer from "../components/PaymentProofViewer";
import DepositVerificationPanel from "../components/DepositVerificationPanel";
import QuoteRejectionModal from "../components/QuoteRejectionModal";
import BookingConfirmationModal from "../components/BookingConfirmationModal";
import BookingConfirmationTemplatesModal from "../../bookings/components/BookingConfirmationTemplatesModal";
import CommunicationTemplateGenerator from "../../leads/components/CommunicationTemplateGenerator";
import FollowUpMention from "../../followups/components/FollowUpMention.jsx";
import QuoteFollowUpAutomationCard from "../components/QuoteFollowUpAutomationCard.jsx";
import QuoteDeliveryStatus from "../components/QuoteDeliveryStatus.jsx";
import QuoteUpdateRequestPanel from "../components/QuoteUpdateRequestPanel";
import QuoteDetailWorkspace from "../components/QuoteDetailWorkspace.jsx";
import { canSendQuoteToCustomer } from "../utils/quotePdfReadiness.js";
import QuoteOperationalStatusBadge from "../components/QuoteOperationalStatusBadge.jsx";
import { markQuoteAsSent } from "../services/quoteActionService";
import { ConfigProvider } from "antd";
import { bubakiiTheme } from "../../../theme/bubakiiTheme.js";
import { useAuth } from "../../../contexts/AuthContext";
import { useToast } from "../../../shared/toast";
import CustomerNotesPanel from "../../customers/components/CustomerNotesPanel.jsx";
import { getLead } from "../../leads/services/leadService.js";
import { getInvoices } from "../../invoices/services/invoiceService.js";
import { message } from "antd";
import styled from "styled-components";

const OpsTabsWrap = styled(Box)`
  &#quote-ops-tabs {
    width: 100%;
  }
`;
import {
  areQuotePdfsReady,
  getQuotePdfStatus,
  getQuoteItemsListPdfStatus,
} from "../utils/quotePdfReadiness.js";
import { waitForQuoteEmailDelivery } from "../services/waitForQuoteEmailDelivery.js";
import {
  JobJourneyStrip,
  seedFromQuote,
  useJobJourneyLinks,
} from "../../../shared/jobJourney/index.js";
import EstimatePricingFab from "../components/EstimatePricingFab.jsx";

/** Infer exclusive vs share move type from quote share signals (not size loadType alone). */
const resolveQuoteMoveType = (quote) => {
  if (!quote) return "exclusive_load";
  if (quote.loadType === "share_load") return "share_load";
  if (quote.quoteType === "SL") return "share_load";
  if (quote.delivery?.type === "share_load") return "share_load";
  if (/-SL$/i.test(String(quote.referenceNumber || ""))) return "share_load";
  return "exclusive_load";
};

/**
 * QuoteDetailsView Component
 */
const QuoteDetailsView = () => {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, permissions } = useAuth();
  const toast = useToast();

  // Get initial quote data from location state (optimistic update)
  const initialQuote = location.state?.quote;

  // State
  const [quote, setQuote] = useState(initialQuote || null);
  const [loading, setLoading] = useState(!initialQuote); // Don't show loading if we have initial data
  const [error, setError] = useState(null);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [rejectionModalVisible, setRejectionModalVisible] = useState(false);
  const [bookingModalVisible, setBookingModalVisible] = useState(false);
  const [bookingTemplatesModalVisible, setBookingTemplatesModalVisible] =
    useState(false);
  const [markSentLoading, setMarkSentLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [policyActionLoading, setPolicyActionLoading] = useState(false);
  const [policyGloballyEnabled, setPolicyGloballyEnabled] = useState(false);
  const [existingInvoiceId, setExistingInvoiceId] = useState(null);
  const [linkedLead, setLinkedLead] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
    action: null,
  });
  const [opsTab, setOpsTab] = useState(0);
  const [verifyOpenNonce, setVerifyOpenNonce] = useState(0);

  const jobJourneyLinks = useJobJourneyLinks(
    quote
      ? seedFromQuote(quote, {
          invoiceId: existingInvoiceId,
          invoiceDisplay: existingInvoiceId,
        })
      : { current: "quote", enabled: false }
  );

  // Fetch quote data
  const fetchQuote = useCallback(async () => {
    if (!quoteId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await getQuoteById(quoteId);
      if (response.success) {
        setQuote(response.data);
        const leadId = response.data?.leadId;
        if (leadId) {
          try {
            const leadRes = await getLead(leadId);
            setLinkedLead(leadRes?.success ? leadRes.data : null);
          } catch (leadErr) {
            console.error("Error fetching linked lead:", leadErr);
            setLinkedLead(null);
          }
        } else {
          setLinkedLead(null);
        }
      } else {
        setError(response.error || "Failed to load quote");
      }
    } catch (err) {
      console.error("Error fetching quote:", err);
      setError(err.message || "Failed to load quote");
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  const quotePdfStatus = getQuotePdfStatus(quote);
  const quoteItemsListPdfStatus = getQuoteItemsListPdfStatus(quote);
  const isQuotePdfReady = areQuotePdfsReady(quote);

  const getQuotePdfMessage = () => {
    if (!quote) return null;
    if (quotePdfStatus === "deleted" || quoteItemsListPdfStatus === "deleted") {
      return "PDFs were removed because this quote is closed.";
    }
    if (quotePdfStatus === "failed" || quoteItemsListPdfStatus === "failed") {
      return (
        quote?.quotePdf?.error ||
        quote?.quoteItemsListPdf?.error ||
        "Last PDF generation failed. Sending the quote will regenerate PDFs."
      );
    }
    if (!isQuotePdfReady) {
      return "PDFs are generated automatically when you send the quote email.";
    }
    return null;
  };

  useEffect(() => {
    fetchQuote();
  }, [fetchQuote]);

  // Deep-link from Quotes list "Verify Payment" CTA
  useEffect(() => {
    if (!location.state?.openVerification) return;
    setOpsTab(1);
    setVerifyOpenNonce((n) => n + 1);
    requestAnimationFrame(() => {
      document
        .getElementById("quote-ops-tabs")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    // Clear flag so refresh/back doesn't reopen the modal
    navigate(location.pathname, {
      replace: true,
      state: { quote: location.state?.quote },
    });
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const policy = await getQuotePolicy();
        if (!cancelled) setPolicyGloballyEnabled(Boolean(policy?.enabled));
      } catch {
        if (!cancelled) setPolicyGloballyEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Check if invoice already exists for this quote
  useEffect(() => {
    const checkExistingInvoice = async () => {
      if (!quote?.id) {
        setExistingInvoiceId(null); // Reset when quote is not available
        return;
      }

      // Reset to null first, then check
      setExistingInvoiceId(null);

      try {
        const result = await getInvoices({ quoteId: quote.id });

        // Debug logging (only in development)
        if (import.meta.env.DEV) {
          console.log("Invoice check result:", {
            success: result.success,
            dataType: Array.isArray(result.data) ? "array" : typeof result.data,
            dataLength: Array.isArray(result.data) ? result.data.length : "N/A",
            data: result.data,
            quoteId: quote.id,
          });
        }

        // Handle different response structures
        let invoices = [];
        if (result.success) {
          if (Array.isArray(result.data)) {
            invoices = result.data;
          } else if (result.data?.data && Array.isArray(result.data.data)) {
            invoices = result.data.data;
          } else if (
            result.data?.invoices &&
            Array.isArray(result.data.invoices)
          ) {
            invoices = result.data.invoices;
          }
        }

        if (invoices.length > 0) {
          // Strictly find invoice with matching quoteId
          // Only match if quoteId exists and matches exactly
          const matchingInvoice = invoices.find((inv) => {
            // Ensure quoteId exists and is not null/undefined
            if (
              !inv.quoteId ||
              inv.quoteId === null ||
              inv.quoteId === undefined
            ) {
              return false;
            }

            // Exact match with type coercion
            const invQuoteId = String(inv.quoteId).trim();
            const currentQuoteId = String(quote.id).trim();

            return invQuoteId === currentQuoteId;
          });

          if (matchingInvoice) {
            const invoiceId = matchingInvoice.id || matchingInvoice._id;

            // Double-check the invoice actually has the correct quoteId
            if (invoiceId && matchingInvoice.quoteId) {
              setExistingInvoiceId(invoiceId);
              if (import.meta.env.DEV) {
                console.log("Found existing invoice:", {
                  invoiceId,
                  quoteId: matchingInvoice.quoteId,
                  expectedQuoteId: quote.id,
                });
              }
            } else {
              if (import.meta.env.DEV) {
                console.warn(
                  "Invoice found but missing ID or quoteId:",
                  matchingInvoice,
                );
              }
              setExistingInvoiceId(null);
            }
          } else {
            if (import.meta.env.DEV) {
              console.log(
                "No matching invoice found for quoteId:",
                quote.id,
                "Available invoices:",
                invoices.map((inv) => ({
                  id: inv.id || inv._id,
                  quoteId: inv.quoteId,
                  quoteIdType: typeof inv.quoteId,
                })),
              );
            }
            setExistingInvoiceId(null);
          }
        } else {
          setExistingInvoiceId(null);
        }
      } catch (err) {
        console.error("Error checking for existing invoice:", err);
        // Reset to null on error so button can show
        setExistingInvoiceId(null);
      }
    };

    checkExistingInvoice();
  }, [
    quote?.id,
    quote?.status,
    quote?.payment?.depositVerified,
  ]);

  // Handlers
  const handleBack = useCallback(() => {
    navigate("/quotes");
  }, [navigate]);

  const handleEdit = useCallback(() => {
    navigate(`/quotes/${quoteId}/edit`, {
      state: { from: { pathname: location.pathname, search: location.search } },
    });
  }, [navigate, quoteId, location.pathname, location.search]);

  const handleDownload = useCallback(async () => {
    if (!isQuotePdfReady) {
      setNotification({
        open: true,
        message: getQuotePdfMessage() || "Quote PDF is not ready yet.",
        severity: "info",
      });
      return;
    }
    try {
      await quoteDownloadService.downloadPdf(quoteId);
      setNotification({
        open: true,
        message: "Quote download started successfully",
        severity: "success",
      });
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || "Failed to download quote",
        severity: "error",
      });
    }
  }, [quoteId, isQuotePdfReady]);

  const handleDownloadItemsList = useCallback(async () => {
    try {
      await quoteDownloadService.downloadItemsList(quoteId);
      setNotification({
        open: true,
        message: "Items list download started successfully",
        severity: "success",
      });
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || "Failed to download items list",
        severity: "error",
      });
    }
  }, [quoteId]);

  const handleNotificationClose = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false, action: null }));
  }, []);

  const handleCancelQueuedSend = useCallback(async () => {
    if (!quote?.id) return;
    try {
      const result = await cancelQueuedQuoteSend(quote.id);
      if (result.success) {
        toast.success("Scheduled send cancelled");
        fetchQuote();
      } else {
        toast.error(result.error || "Could not cancel send", {
          sticky: true,
          href: `/quotes/${quote.id}`,
          linkLabel: "Open quote",
        });
      }
    } catch (err) {
      toast.error(err.message || "Could not cancel send", {
        sticky: true,
        href: `/quotes/${quote.id}`,
        linkLabel: "Open quote",
      });
    }
  }, [quote?.id, fetchQuote, toast]);

  const handleFollowUpNavigate = useCallback(() => {
    if (!quote?.id) return;
    const followUpId =
      quote?.followUpSummary?.nextFollowUpId ||
      quote?.followUpSummary?.latestFollowUpId;
    if (followUpId) {
      navigate(`/followups/${followUpId}`);
    } else {
      navigate(`/followups?entityType=quote&entityId=${quote.id}`);
    }
  }, [navigate, quote]);

  const runAsyncQuoteEmailSend = useCallback(
    async (sendFn, { isResend = false } = {}) => {
      if (!quote?.customerEmail) {
        toast.error("Customer email is required to send this quote", {
          sticky: true,
          href: `/quotes/${quote?.id || quoteId}`,
          linkLabel: "Open quote",
        });
        return;
      }

      if (!isResend && !canSendQuoteToCustomer(quote)) {
        toast.error(
          quote?.automatedPreparation
            ? "Approve Ready to Send review before sending this quote"
            : "Quote is not ready to send",
          {
            sticky: true,
            href: `/quotes/${quote.id}`,
            linkLabel: "Open quote",
          }
        );
        return;
      }

      setSendingEmail(true);
      const startedAt = new Date().toISOString();
      const toastKey = `quote-email-${quote.id}-${startedAt}`;
      toast.info(
        isResend ? "Resending quote email…" : "Sending quote email…",
        {
          key: toastKey,
          duration: 3,
          description: "PDFs will be regenerated and emailed shortly.",
        }
      );

      try {
        const response = await sendFn();

        if (!response.success) {
          toast.error(response.error || "Failed to queue quote email", {
            sticky: true,
            href: `/quotes/${quote.id}`,
            linkLabel: "Open quote",
            key: toastKey,
          });
          return;
        }

        // Legacy undo path (only when WhatsApp delivery is re-enabled)
        if (response.queued && response.undoUntil) {
          setNotification({
            open: true,
            message: `Send scheduled for ${new Date(response.undoUntil).toLocaleTimeString()}. Cancel if needed.`,
            severity: "info",
            action: (
              <Button
                color="inherit"
                size="small"
                onClick={handleCancelQueuedSend}
              >
                Cancel send
              </Button>
            ),
          });
          fetchQuote();
          return;
        }

        const outcome = await waitForQuoteEmailDelivery(quote.id, {
          startedAt,
          campaignId: response.campaignId || null,
        });
        if (outcome.success) {
          toast.success("Quote email sent", { key: toastKey });
          if (outcome.quote) setQuote(outcome.quote);
          else fetchQuote();
        } else {
          toast.error(outcome.error || "Quote email failed", {
            sticky: true,
            href: `/quotes/${quote.id}`,
            linkLabel: "Open quote",
            key: toastKey,
            description: outcome.timedOut
              ? "Check delivery status on the quote."
              : undefined,
          });
          fetchQuote();
        }
      } catch (error) {
        toast.error(error.message || "Failed to send quote email", {
          sticky: true,
          href: `/quotes/${quote.id}`,
          linkLabel: "Open quote",
          key: toastKey,
        });
      } finally {
        setSendingEmail(false);
      }
    },
    [quote, quoteId, toast, fetchQuote, handleCancelQueuedSend]
  );

  // Action handlers
  const handleMarkAsSent = useCallback(async () => {
    if (!quote) return;
    setMarkSentLoading(true);
    try {
      const result = await markQuoteAsSent(quote.id, user?.userId || user?.id);
      if (result.success) {
        setNotification({
          open: true,
          message: "Quote marked as sent successfully",
          severity: "success",
        });
        fetchQuote();
      } else {
        setNotification({
          open: true,
          message: result.error || "Failed to mark quote as sent",
          severity: "error",
        });
      }
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || "Failed to mark quote as sent",
        severity: "error",
      });
    } finally {
      setMarkSentLoading(false);
    }
  }, [quote, user, fetchQuote]);

  const handleRejectionSuccess = useCallback(() => {
    fetchQuote();
    setNotification({
      open: true,
      message: "Quote marked as rejected successfully",
      severity: "success",
    });
  }, [fetchQuote]);

  // Handle creating invoice in popup (opens invoice form)
  const handleCreateInvoiceInPopup = useCallback(() => {
    if (!quote?.id) {
      message.error("Quote ID not found");
      return;
    }

    // Check if quote is verified before allowing invoice creation
    const isVerified =
      quote.status === "deposit_verified" ||
      quote.status === "deposit_paid" ||
      quote.status === "ordered" ||
      quote.payment?.depositVerified === true ||
      quote.payment?.depositPaid === true ||
      quote.status === "accepted";

    if (!isVerified) {
      message.error(
        "Quote must be verified (deposit_verified) before creating an invoice",
      );
      return;
    }

    const invoiceFormUrl = `/invoices/new?quoteId=${quote.id}`;
    const isMobile =
      window.matchMedia("(max-width: 768px)").matches ||
      /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (isMobile) {
      window.open(invoiceFormUrl, "_blank", "noopener,noreferrer");
    } else {
      window.open(
        invoiceFormUrl,
        "_blank",
        "noopener,noreferrer,width=1200,height=800",
      );
    }
  }, [quote]);

  // Handle viewing invoice in popup (similar to follow-ups)
  const handleViewInvoiceInPopup = useCallback(() => {
    if (!existingInvoiceId) return;

    const invoiceUrl = `/invoices/${existingInvoiceId}`;
    const isMobile =
      window.matchMedia("(max-width: 768px)").matches ||
      /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (isMobile) {
      window.open(invoiceUrl, "_blank", "noopener,noreferrer");
    } else {
      window.open(
        invoiceUrl,
        "_blank",
        "noopener,noreferrer,width=1200,height=800",
      );
    }
  }, [existingInvoiceId, quote]);

  // Page structure shows immediately - show loading/error states inline

  // Determine which actions are available (only when quote is loaded)
  // Note: quote acceptance + deposit/order/invoice creation all happen through
  // a single reusable channel - "Verify Deposit (SOP #3)" on the Verification tab.
  const canMarkRejected =
    quote &&
    permissions.includes("quotes:update") &&
    (quote.status === "sent" ||
      quote.status === "negotiating" ||
      quote.status === "pending" ||
      quote.status === "send_queued") &&
    quote.status !== "rejected" &&
    quote.status !== "accepted" &&
    quote.status !== "ordered";

  const canMarkAsSent =
    quote &&
    permissions.includes("quotes:update") &&
    quote.status === "pending" &&
    quote.status !== "sent";

  // Booking creation requires orderId to be available
  const canCreateBooking =
    quote &&
    permissions.includes("quotes:update") &&
    (quote.status === "deposit_verified" ||
      quote.status === "deposit_paid" ||
      quote.status === "ordered") &&
    (quote.orderId || quote.payment?.orderId);

  // Invoice creation requires quote to be verified (deposit_verified)
  const isQuoteVerified =
    quote &&
    (quote.status === "deposit_verified" ||
      quote.status === "deposit_paid" ||
      quote.status === "ordered" ||
      quote.payment?.depositVerified === true ||
      quote.payment?.depositPaid === true ||
      quote.status === "accepted");

  const canCreateInvoice = quote && !existingInvoiceId && isQuoteVerified;

  const canQuickVerifyDeposit =
    quote &&
    permissions.includes("quotes:update") &&
    ["accepted", "pending_payment", "pending", "sent", "approved"].includes(
      quote.status
    ) &&
    !quote.payment?.depositVerified;

  const canQuickViewReceipt =
    quote &&
    (quote.payment?.depositVerified || quote.status === "deposit_verified");

  const handleQuickVerifyDeposit = () => {
    setOpsTab(1);
    setVerifyOpenNonce((n) => n + 1);
    requestAnimationFrame(() => {
      document
        .getElementById("quote-ops-tabs")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const canViewBookingTemplates =
    quote &&
    permissions.includes("quotes:update") &&
    (quote.status === "booked" ||
      quote.status === "scheduled" ||
      quote.status === "confirmed") &&
    (quote.orderId || quote.payment?.orderId);

  // View Booking Details only when a booking is actually expected to exist
  // (same status gating as templates)
  const canViewBookingDetails =
    quote &&
    permissions.includes("quotes:view") &&
    (quote.status === "booked" ||
      quote.status === "scheduled" ||
      quote.status === "confirmed") &&
    (quote.orderId || quote.payment?.orderId);

  // Email send/resend permissions - toggle based on whether quote has been sent before
  const hasBeenSent = quote?.sentAt != null; // Check if quote has been sent (sentAt timestamp exists)
  const isSendQueued = quote?.status === "send_queued";

  const canSendEmail =
    quote &&
    permissions.includes("quotes:send") &&
    !hasBeenSent &&
    !isSendQueued &&
    quote.status !== "accepted" &&
    quote.status !== "rejected" &&
    quote.customerEmail;

  const canResendEmail =
    quote &&
    permissions.includes("quotes:send") &&
    hasBeenSent &&
    quote.status !== "accepted" &&
    quote.status !== "rejected" &&
    quote.customerEmail;

  const policyAgreementStatus = quote?.customerPolicyAgreement?.status;
  const policyAgreed = policyAgreementStatus === "agreed";
  const showPolicyActions =
    quote &&
    permissions.includes("quotes:send") &&
    (policyGloballyEnabled ||
      policyAgreementStatus === "pending" ||
      policyAgreed ||
      quote?.customerPolicyAgreement?.requiredAt != null);

  const handleEmailPolicyAgreement = useCallback(async () => {
    if (!quote?.id) return;
    setPolicyActionLoading(true);
    try {
      await emailPolicyAgreement(quote.id);
      message.success("Policy agreement email queued");
      await fetchQuote();
    } catch (err) {
      message.error(err.message || "Failed to email policy agreement");
    } finally {
      setPolicyActionLoading(false);
    }
  }, [quote?.id, fetchQuote]);

  const handleCopyPolicyLink = useCallback(async () => {
    if (!quote?.id) return;
    setPolicyActionLoading(true);
    try {
      const link = await createPolicyShareLink(quote.id);
      const url = link?.shareableUrl;
      if (!url) {
        throw new Error("No shareable URL returned");
      }
      await navigator.clipboard.writeText(url);
      message.success("Policy agreement link copied");
      await fetchQuote();
    } catch (err) {
      message.error(err.message || "Failed to copy policy link");
    } finally {
      setPolicyActionLoading(false);
    }
  }, [quote?.id, fetchQuote]);

  const handleSendQuoteEmail = useCallback(async () => {
    if (!quote) return;
    await runAsyncQuoteEmailSend(() =>
      sendQuote(quote.id, {
        message: "",
        estimatedCost: quote.estimatedCost || quote.totalCost,
        serviceType: quote.serviceType || "standard",
        notes: "",
        loadType: resolveQuoteMoveType(quote),
      })
    );
  }, [quote, runAsyncQuoteEmailSend]);

  const handleResendQuoteEmail = useCallback(async () => {
    if (!quote) return;
    await runAsyncQuoteEmailSend(
      () =>
        resendQuote(quote.id, {
          message: "",
          estimatedCost: quote.estimatedCost || quote.totalCost,
          serviceType: quote.serviceType || "standard",
          notes: "",
          loadType: resolveQuoteMoveType(quote),
          fullResend: true,
        }),
      { isResend: true }
    );
  }, [quote, runAsyncQuoteEmailSend]);

  return (
    <Container maxWidth="xl">
      <Box py={3}>
        {/* Error state - show inline */}
        {error && !quote && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Loading state - show inline */}
        {loading && !quote && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
            mb={3}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Header - Two Row Layout */}
        <Box mb={3}>
          {/* Row 1: Back + Title */}
          <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
            <Button startIcon={<ArrowBack />} onClick={handleBack}>
              Back to Quotes
            </Button>
            <Typography variant="h4" component="h1">
              Quote Details
            </Typography>
            {quote && <QuoteOperationalStatusBadge quote={quote} />}
          </Box>

          <JobJourneyStrip
            current="quote"
            customerId={jobJourneyLinks.customerId}
            leadId={jobJourneyLinks.leadId}
            quoteId={jobJourneyLinks.quoteId}
            invoiceId={jobJourneyLinks.invoiceId}
            orderId={jobJourneyLinks.orderId}
            customerDisplay={jobJourneyLinks.customerDisplay}
            leadDisplay={jobJourneyLinks.leadDisplay}
            quoteDisplay={jobJourneyLinks.quoteDisplay}
            invoiceDisplay={jobJourneyLinks.invoiceDisplay}
            orderDisplay={jobJourneyLinks.orderDisplay}
          />

          {/* Debug Panel - Only in development */}
          {import.meta.env.DEV && quote && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="caption" component="div">
                <strong>🔍 Debug Info:</strong> Status:{" "}
                <code>{quote.status}</code> | Order ID:{" "}
                <code>{quote.orderId || quote.payment?.orderId || "None"}</code>{" "}
                | Can Create Booking: {canCreateBooking ? "✅ Yes" : "❌ No"} |
                Has Permission:{" "}
                {permissions.includes("quotes:update") ? "✅" : "❌"}
              </Typography>
            </Alert>
          )}

          {getQuotePdfMessage() && (
            <Alert
              severity={isQuotePdfReady ? "success" : "info"}
              sx={{ mb: 2 }}
            >
              {getQuotePdfMessage()}
            </Alert>
          )}

          {/* Row 2: Actions */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            {/* Primary Actions */}
            <Box display="flex" gap={1} flexWrap="wrap">
              {canMarkRejected && (
                <Button
                  startIcon={<Cancel />}
                  onClick={() => setRejectionModalVisible(true)}
                  color="error"
                  variant="outlined"
                  size="medium"
                >
                  Mark Rejected
                </Button>
              )}

              {canMarkAsSent && (
                <Button
                  startIcon={<Send />}
                  onClick={handleMarkAsSent}
                  disabled={markSentLoading}
                  color="primary"
                  variant="contained"
                  size="medium"
                >
                  Mark as Sent
                </Button>
              )}

              {canQuickVerifyDeposit && (
                <Button
                  startIcon={<FactCheck />}
                  onClick={handleQuickVerifyDeposit}
                  color="success"
                  variant="contained"
                  size="medium"
                  data-testid="quick-verify-deposit-button"
                  sx={{
                    backgroundColor: "#52c41a",
                    "&:hover": { backgroundColor: "#389e0d" },
                  }}
                >
                  Verify Deposit
                </Button>
              )}

              {canQuickViewReceipt && (
                <Button
                  startIcon={<Receipt />}
                  onClick={handleQuickVerifyDeposit}
                  color="success"
                  variant="outlined"
                  size="medium"
                  data-testid="quick-view-receipt-button"
                >
                  View Deposit Receipt
                </Button>
              )}

              {canCreateBooking && (
                <Button
                  startIcon={<Schedule />}
                  onClick={() => setBookingModalVisible(true)}
                  color="primary"
                  variant="contained"
                  size="medium"
                >
                  Create Booking
                </Button>
              )}

              {canViewBookingTemplates && (
                <Button
                  startIcon={<Message />}
                  onClick={() => setBookingTemplatesModalVisible(true)}
                  color="primary"
                  variant="outlined"
                  size="medium"
                >
                  View Booking Templates
                </Button>
              )}

              {/* View Booking Details - show whenever there's an order */}
              {canViewBookingDetails && (
                <Button
                  startIcon={<Schedule />}
                  onClick={() => {
                    const orderId = quote.orderId || quote.payment?.orderId;
                    if (orderId) {
                      navigate(`/bookings/${orderId}`, {
                        state: {
                          source: "quote",
                          sourceId: quote.id || quoteId,
                          sourcePath: location.pathname,
                          booking: quote.booking, // Pass booking data if available for optimistic update
                        },
                      });
                    }
                  }}
                  color="primary"
                  variant="outlined"
                  size="medium"
                >
                  View Booking Details
                </Button>
              )}

              {/* Email Send/Resend Buttons */}
              {isSendQueued && permissions.includes("quotes:send") && (
                <Button
                  variant="outlined"
                  color="warning"
                  size="medium"
                  onClick={handleCancelQueuedSend}
                >
                  Cancel scheduled send
                </Button>
              )}

              {canSendEmail && (
                <Button
                  startIcon={<Email />}
                  onClick={handleSendQuoteEmail}
                  disabled={sendingEmail}
                  color="primary"
                  variant="outlined"
                  size="medium"
                >
                  {sendingEmail ? "Sending..." : "Send Email"}
                </Button>
              )}

              {canResendEmail && (
                <Button
                  startIcon={<Email />}
                  onClick={handleResendQuoteEmail}
                  disabled={sendingEmail}
                  color="primary"
                  variant="outlined"
                  size="medium"
                >
                  {sendingEmail ? "Resending..." : "Resend Email"}
                </Button>
              )}

              {showPolicyActions && (
                <>
                  {policyAgreed ? (
                    <Chip
                      icon={<Policy />}
                      label={
                        quote?.customerPolicyAgreement?.locationMismatch
                          ? "Policy agreed · location mismatch"
                          : "Policy agreed"
                      }
                      color={
                        quote?.customerPolicyAgreement?.locationMismatch
                          ? "warning"
                          : "success"
                      }
                      size="small"
                      variant="outlined"
                    />
                  ) : policyAgreementStatus === "pending" ? (
                    <Chip
                      icon={<Policy />}
                      label="Policy pending"
                      color="warning"
                      size="small"
                      variant="outlined"
                    />
                  ) : null}
                  <Button
                    startIcon={<Email />}
                    onClick={handleEmailPolicyAgreement}
                    disabled={policyActionLoading}
                    color="primary"
                    variant="outlined"
                    size="medium"
                  >
                    Email policy agreement
                  </Button>
                  <Button
                    startIcon={<LinkIcon />}
                    onClick={handleCopyPolicyLink}
                    disabled={policyActionLoading}
                    color="primary"
                    variant="outlined"
                    size="medium"
                  >
                    Copy policy link
                  </Button>
                </>
              )}

              {/* Create Invoice Button - show only when quote is verified and no invoice exists */}
              {canCreateInvoice && (
                <Button
                  startIcon={<Receipt />}
                  onClick={handleCreateInvoiceInPopup}
                  color="primary"
                  variant="contained"
                  size="medium"
                >
                  Create Invoice
                </Button>
              )}

              {/* View Invoice Button - show if invoice already exists, opens in popup */}
              {existingInvoiceId && (
                <Button
                  startIcon={<Receipt />}
                  variant="outlined"
                  color="info"
                  size="medium"
                  onClick={handleViewInvoiceInPopup}
                >
                  View Invoice
                </Button>
              )}
            </Box>

            {/* Secondary Actions */}
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
              <EstimatePricingFab
                leadId={quote?.leadId || linkedLead?.id || linkedLead?._id || null}
                quoteId={quote?.id || quoteId}
                formSnapshot={{
                  from:
                    quote?.from ||
                    linkedLead?.moving_from ||
                    linkedLead?.from,
                  to:
                    quote?.to ||
                    linkedLead?.moving_to ||
                    linkedLead?.to,
                  serviceType: quote?.serviceType || quote?.loadType,
                  items: quote?.items,
                  itemsList: quote?.itemsList,
                }}
              />
              <FollowUpMention
                summary={quote?.followUpSummary}
                onClick={handleFollowUpNavigate}
                size="medium"
                variant="filled"
              />
              <Tooltip title="Generate Message">
                <IconButton
                  onClick={() => setTemplateDialog(true)}
                  color="primary"
                  size="medium"
                >
                  <Message />
                </IconButton>
              </Tooltip>

              {permissions.includes("quotes:update") && (
                <Tooltip title="Edit Quote">
                  <IconButton
                    onClick={handleEdit}
                    color="primary"
                    size="medium"
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              )}

              {isQuotePdfReady && (
                <Tooltip title="Download Quote">
                  <IconButton
                    onClick={handleDownload}
                    color="primary"
                    size="medium"
                  >
                    <Download />
                  </IconButton>
                </Tooltip>
              )}

              {isQuotePdfReady && (
                <Tooltip title="Download Items List">
                  <IconButton
                    onClick={handleDownloadItemsList}
                    color="primary"
                    size="medium"
                  >
                    <ListAlt />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title="Create Follow-up">
                <IconButton
                  onClick={() =>
                    navigate(
                      `/followups/new?entityType=quote&entityId=${quoteId}`,
                    )
                  }
                  color="primary"
                  size="medium"
                >
                  <Schedule />
                </IconButton>
              </Tooltip>

              {quote?.customerEmail && !isSendQueued && (
                <Tooltip title="Send quote email">
                  <IconButton
                    onClick={handleSendQuoteEmail}
                    color="primary"
                    size="medium"
                    disabled={sendingEmail}
                  >
                    <Email />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>

        {/* Quote Update Request Panel - Show if pending update request exists */}
        {quote?.updateRequest?.status === "pending" && (
          <QuoteUpdateRequestPanel
            quoteId={quote.id}
            request={quote.updateRequest}
            onResolved={() => fetchQuote()}
          />
        )}

        {/* Show content only when quote is loaded */}
        {quote ? (
          <QuoteDetailWorkspace
            quote={quote}
            linkedLead={linkedLead}
            onOpenLead={(leadId) => navigate(`/leads/${leadId}`)}
            onPreviewPdf={() => {
              if (isQuotePdfReady) {
                handleDownload();
              } else {
                toast.info(
                  "PDFs are generated when you send the quote email. Download will be available after send."
                );
              }
            }}
            pdfReady={isQuotePdfReady}
            pdfBusy={false}
            onPreparationApproved={() => fetchQuote()}
            onRevisionCreated={(revisionId) => {
              if (revisionId) navigate(`/quotes/${revisionId}`);
            }}
            customerNotesSlot={
              quote.customerId ? (
                <ConfigProvider theme={bubakiiTheme}>
                  <CustomerNotesPanel
                    customerId={quote.customerId}
                    context="quote"
                    relatedId={quote.id}
                    entityType="quote"
                  />
                </ConfigProvider>
              ) : null
            }
            opsToolsSlot={
              <Box display="flex" flexDirection="column" gap={2}>
                <QuoteDeliveryStatus
                  lastDelivery={quote.lastDelivery}
                  quoteStatus={quote.status}
                />
                <QuoteFollowUpAutomationCard
                  quoteId={quote.id}
                  quoteSentAt={quote.sentAt}
                  canManage={permissions.includes("quotes:update")}
                />
                <OpsTabsWrap id="quote-ops-tabs">
                  <Tabs
                    value={opsTab}
                    onChange={(_, value) => setOpsTab(value)}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{
                      mb: 2,
                      borderBottom: 1,
                      borderColor: "divider",
                      minHeight: 44,
                      "& .MuiTab-root": {
                        minHeight: 44,
                        textTransform: "none",
                        fontWeight: 600,
                      },
                    }}
                  >
                    <Tab
                      icon={<Payments fontSize="small" />}
                      iconPosition="start"
                      label="Payment"
                    />
                    <Tab
                      icon={<VerifiedUser fontSize="small" />}
                      iconPosition="start"
                      label="Verification"
                    />
                    <Tab
                      icon={<ManageAccounts fontSize="small" />}
                      iconPosition="start"
                      label="Management"
                    />
                  </Tabs>

                  <ConfigProvider theme={bubakiiTheme}>
                    {opsTab === 0 && <PaymentProofViewer quote={quote} />}

                    {opsTab === 1 && (
                      <DepositVerificationPanel
                        quote={quote}
                        onUpdate={() => {
                          fetchQuote();
                        }}
                        userRole={user?.role || "user"}
                        userId={user?.userId || user?.id}
                        canManage={permissions.includes("quotes:update")}
                        openVerifyNonce={verifyOpenNonce}
                      />
                    )}

                    {opsTab === 2 && (
                      <QuoteStatusManager
                        quote={quote}
                        onUpdate={(updatedQuote) => {
                          setQuote(updatedQuote);
                          fetchQuote();
                        }}
                        userRole={user?.role || "user"}
                        userId={user?.userId || user?.id}
                        canManage={permissions.includes("quotes:update")}
                      />
                    )}
                  </ConfigProvider>
                </OpsTabsWrap>
              </Box>
            }
          />
        ) : (
          !loading &&
          !error && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Quote not found
            </Alert>
          )
        )}
      </Box>

      {/* Communication Template Generator Dialog */}
      <ConfigProvider theme={bubakiiTheme}>
        <CommunicationTemplateGenerator
          quoteId={quoteId}
          visible={templateDialog}
          onClose={() => setTemplateDialog(false)}
        />
      </ConfigProvider>

      {/* Booking Confirmation Modal */}
      {quote && (
        <ConfigProvider theme={bubakiiTheme}>
          <BookingConfirmationModal
            visible={bookingModalVisible}
            quote={quote}
            orderId={quote.orderId || quote.payment?.orderId}
            onClose={() => setBookingModalVisible(false)}
            onSuccess={(bookingData) => {
              fetchQuote(); // Refresh quote data
              setNotification({
                open: true,
                message: "Booking created successfully!",
                severity: "success",
              });
              // Optionally open templates modal after booking creation
              if (bookingData?.booking) {
                setTimeout(() => {
                  setBookingTemplatesModalVisible(true);
                }, 500);
              }
            }}
            userId={user?.userId || user?.id}
          />
        </ConfigProvider>
      )}

      {/* Booking Confirmation Templates Modal */}
      {quote && (
        <ConfigProvider theme={bubakiiTheme}>
          <BookingConfirmationTemplatesModal
            visible={bookingTemplatesModalVisible}
            orderId={quote.orderId || quote.payment?.orderId}
            booking={quote.order?.booking || quote.booking}
            onClose={() => setBookingTemplatesModalVisible(false)}
            onConfirmationUpdate={() => {
              fetchQuote(); // Refresh quote data
            }}
            userId={user?.userId || user?.id}
          />
        </ConfigProvider>
      )}

      {/* Quote Rejection Modal */}
      {quote && (
        <ConfigProvider theme={bubakiiTheme}>
          <QuoteRejectionModal
            visible={rejectionModalVisible}
            quote={quote}
            onClose={() => setRejectionModalVisible(false)}
            onSuccess={handleRejectionSuccess}
            userId={user?.userId || user?.id}
          />
        </ConfigProvider>
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
      >
        <Alert
          onClose={handleNotificationClose}
          severity={notification.severity}
          action={notification.action || undefined}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default QuoteDetailsView;
